import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FiBriefcase, FiCheckCircle } from "react-icons/fi";
import { LuUserRoundCheck } from "react-icons/lu";

import editProfileUser from "../../../assets/images/others/host-side-images/editProfileUSer.png";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "../../../utils/helpers/enums/routes";
import { CompleteAmbassadorProfileDTO } from "utils/helpers/models/ambassador/complete-profile.dto";
import useAuth from "containers/auth/useHooks";
import useStore from "hooks/useStore";
import { ReactComponent as EditPic } from "assets/images/svgs/edit-profiles.svg";
import useAmbassador from "../useHooks";
import MultiselectField from "components/forms/multiselect-field";
import { GENDERS, LANGUAGES, SKILLS } from "utils/helpers/enums/shared.enum";
import PhoneNumberInput from "components/phone-number-input";

const AmbassadorProfile: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    trigger,
    setError,
    control,
  } = useForm<CompleteAmbassadorProfileDTO>();
  const skills = [
    { id: 1, title: SKILLS.COMMUNICATION },
    { id: 2, title: SKILLS.SOCIAL_MEDIA },
    { id: 3, title: SKILLS.EVENT_ORGANIZATION },
    { id: 4, title: SKILLS.YOUTH_ENGAGEMENT },
    { id: 5, title: SKILLS.EDUCATION_COACHING },
    { id: 6, title: SKILLS.COMMUNITY_OUTREACH },
  ];
  const commitmentOptions = ["THREE_MONTHS", "SIX_MONTHS", "NINE_PLUS_MONTHS"];
  const engagementOptions = [
    "MINIMUM_5_6_HOURS_PER_MONTH",
    "MODERATE_10_15_HOURS_PER_MONTH",
    "HIGH_15_PLUS_HOURS_PER_MONTH",
  ];
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [text, setText] = useState<string>("");
  const maxLength = 60;
  const { setAmbassadorCompletionData } = useStore();
  const { getStates, uploadFile } = useAuth();
  const { updateAmbassadorProfile, updateUserProfile } = useAmbassador();
  const [currentStep, setCurrentStep] = useState(1);
  const [profileImage, setProfileImage] = useState<string | null>();
  const [formData, setFormData] = useState<CompleteAmbassadorProfileDTO>(
    new CompleteAmbassadorProfileDTO({ profilePicId: 0 })
  );
  const [oldFormData, setOldFormData] = useState<CompleteAmbassadorProfileDTO>(
    new CompleteAmbassadorProfileDTO({ profilePicId: 0 })
  );

  const navigate = useNavigate();

  const handleImageDelete = () => {
    setValue("profilePicUrl", "");
    setValue("profilePicId", 0);
    setFormData({ ...formData, profilePicUrl: "", profilePicId: 0 });
    setProfileImage(null);

    // Clear any errors when deleting image
    clearErrors("profilePicUrl");
    clearErrors("profilePicId");
  };

  // const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = event.target.files[0];
  //   if (file) {
  //     setFormData({ ...formData, profilePicUrl: URL.createObjectURL(file) });
  //     setValue("profilePicUrl", file, { shouldValidate: true });
  //   }
  // };
  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setValue("profilePicUrl", file);
      setValue("profilePicId", 0); // Reset to 0 when new image is selected
      setFormData({
        ...formData,
        profilePicUrl: URL.createObjectURL(file),
        profilePicId: 0,
      });
      setProfileImage(URL.createObjectURL(file));

      // Clear any existing errors
      clearErrors("profilePicUrl");
      clearErrors("profilePicId");
    }
  };

  const handleChange = async (event: any) => {
    const { value, name } = event.target;
    formData[name] = value;
    setValue(name, formData[name], { shouldValidate: true });

    if (name === "state") {
      formData.city = "";
      setValue("city", "");
      clearErrors("city");
    }
    setFormData({ ...formData });

    // For zipCode, trigger validation asynchronously and clear error if valid
    if (name === "zipCode") {
      const isValid = await trigger("zipCode");
      if (isValid) {
        clearErrors("zipCode");
      }
    } else if (errors[name]) {
      // Clear errors for other fields when user starts typing
      clearErrors(name);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleNext = async (data: CompleteAmbassadorProfileDTO) => {
    if (currentStep === 1) {
      const profilePic = data.profilePicUrl;
      if (!profilePic) {
        setError("profilePicUrl", {
          type: "required",
          message: "Please upload your profile picture",
        });
        return;
      }

      const fields = [
        "profilePicUrl",
        "profilePicId",
        "firstName",
        "lastName",
        "phone",
        "language",
        "dob",
        "gender",
        "email",
        "city",
        "address",
        "state",
        "zipCode",
      ];
      const changed = hasChanged(fields);
      if (changed) {
        const profilePic = data.profilePicUrl;
        if (typeof profilePic !== "string") {
          const imageData: any = await uploadFile(profilePic);
          console.log(imageData, "imageData");
          const profilePicId = Number(imageData?.id) || 0;
          formData.profilePicId = profilePicId;
          data.profilePicId = profilePicId;
          setValue("profilePicId", profilePicId);
        } else {
          // Use existing profilePicId if available
          const profilePicId = Number(formData.profilePicId) || 0;
          data.profilePicId = profilePicId;
          setValue("profilePicId", profilePicId);
        }
        updateUserProfile(data, formData, setOldFormData, setCurrentStep);
      } else {
        // Even if no changes, ensure profilePicId is set
        const profilePicId = Number(formData.profilePicId) || 0;
        data.profilePicId = profilePicId;
        setValue("profilePicId", profilePicId);
        setCurrentStep(currentStep + 1);
      }
    } else {
      const fields = [
        "skills",
        "mission",
        "engagement",
        "commitment",
        "referralCode",
      ];

      const changed = hasChanged(fields);
      if (changed) {
        updateAmbassadorProfile(formData);
      }
    }
  };

  const hasChanged = (fields: string[]) => {
    let changed = false;
    for (let key in formData) {
      if (fields.includes(key)) {
        if (Array.isArray(formData[key])) {
          if (
            !(
              formData[key].length === oldFormData[key].length &&
              formData[key].every((i) => oldFormData[key].includes(i))
            )
          ) {
            changed = true;
            break;
          }
        } else {
          if (formData[key] !== oldFormData[key]) {
            changed = true;
            break;
          }
        }
      }
    }

    return changed;
  };

  useEffect(() => {
    if (formData.state) {
      const state = states.find((s) => s.name === formData.state);
      console.log(state, "state");
      if (state) {
        setCities([...state.cities]);
      }
    }
  }, [states, formData.state]);

  useEffect(() => {
    setAmbassadorCompletionData(formData, setFormData, setValue);
    setAmbassadorCompletionData(oldFormData, setOldFormData, setValue);
    getStates(setStates);

    // Ensure profilePicId is always a number
    if (formData.profilePicId) {
      formData.profilePicId = Number(formData.profilePicId);
      setFormData({ ...formData });
    }

    // Set profile image preview if profilePicUrl exists in formData
    if (formData.profilePicUrl && typeof formData.profilePicUrl === "string") {
      setProfileImage(formData.profilePicUrl);
    }
  }, []);

  // Watch for changes in formData.profilePicUrl to update profileImage
  useEffect(() => {
    if (formData.profilePicUrl && typeof formData.profilePicUrl === "string") {
      setProfileImage(formData.profilePicUrl);
    }
  }, [formData.profilePicUrl]);

  const steps = [
    { id: 1, title: "Ambassador", icon: <LuUserRoundCheck size={20} /> },
    { id: 2, title: "More", icon: <FiBriefcase size={20} /> },
  ];

  const onSelectSkills = (skills: string[]) => {
    formData.skills = skills;

    console.log(formData.skills, "formData.skills");
    setValue("skills", formData.skills, { shouldValidate: true });
    setFormData({ ...formData });
  };

  console.log(formData, "formData in AmbassadorProfile");

  return (
    <div className="container min-h-screen mt-5 md:mt-10">
      <form onSubmit={handleSubmit(handleNext)}>
        {/* Stepper */}
        <div className="flex items-center mb-8 relative w-full">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className="flex flex-col justify-center w-full sm:w-1/2 md:w-2/12"
            >
              <div className="flex items-center w-full">
                <div
                  className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${
                    currentStep > step.id
                      ? "border-ambassadorBlue bg-ambassadorBlue "
                      : currentStep === step.id
                      ? "border-ambassadorBlue bg-white text-ambassadorBlue"
                      : "border-grayDarkText bg-white text-grayDarkText"
                  }`}
                >
                  {currentStep > step.id ? (
                    <FiCheckCircle className="text-white" />
                  ) : (
                    step.icon
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 ${
                      currentStep > step.id
                        ? "bg-ambassadorBlue"
                        : currentStep === step.id
                        ? "bg-grayDarkText"
                        : "bg-grayDarkText"
                    }`}
                  ></div>
                )}
              </div>
              <p
                className={`mt-2 ml-[-10px] text-sm text-start font-semibold ${
                  currentStep === step.id
                    ? "text-ambassadorBlue"
                    : currentStep > step.id
                    ? "text-ambassadorBlue"
                    : "text-gray-500"
                }`}
              >
                {step.title}
              </p>
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="">
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div>
              <div className="flex flex-col items-center w-[270px]">
                <div className="flex flex-col gap-2">
                  <div className="relative inline-block w-36 h-36">
                    <img
                      src={
                        profileImage ||
                        formData.profilePicUrl ||
                        editProfileUser
                      }
                      alt="Profile"
                      className="w-36 h-36 rounded-full object-cover"
                    />
                    {formData.profilePicUrl || profileImage ? (
                      <label
                        htmlFor="profileImage"
                        className="absolute bottom-2 right-0 bg-white shadow p-2 rounded-full cursor-pointer"
                      >
                        <EditPic />
                      </label>
                    ) : (
                      <label
                        htmlFor="profileImage"
                        className="absolute bottom-2 right-0 bg-white shadow p-2 rounded-full cursor-pointer"
                      >
                        <EditPic />
                      </label>
                    )}
                    <input
                      id="profileImage"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      {...register("profilePicUrl", { required: false })}
                      onChange={handleImageChange}
                    />
                    <input
                      type="hidden"
                      {...register("profilePicId", {
                        required: false,
                        valueAsNumber: true,
                      })}
                    />
                  </div>
                  <div className="mt-4">
                    <label className="text-label text-base font-semibold block">
                      Ambassador Profile Picture
                    </label>
                    <p className="text-labelTextColor text-start text-sm mt-1">
                      For security purposes, only use updated/recent picture of
                      ambassador by which host can identify you.
                    </p>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div>
                <div className="w-full my-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="flex flex-col gap-1">
                    <label className="text-label text-base font-semibold">
                      First name
                    </label>
                    <input
                      type="text"
                      className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                      {...register("firstName", {
                        required: "First name is required",
                      })}
                      value={formData.firstName}
                      onChange={handleChange}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-sm">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-label text-base font-semibold">
                      Last name
                    </label>
                    <input
                      type="text"
                      className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                      {...register("lastName", {
                        required: "Last name is required",
                      })}
                      value={formData.lastName}
                      onChange={handleChange}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-sm">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-label text-base font-semibold">
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                      {...register("email", {
                        required: "Email is required", // Ensure the field is not empty
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, // Regex for email validation
                          message: "Invalid email address", // Error message for invalid email format
                        },
                      })}
                      value={formData.email}
                      onChange={handleChange}
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-label text-base font-semibold">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      placeholder="Select Date"
                      className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                      {...register("dob", {
                        required: "Date of Birth is required",
                        validate: (value) => {
                          if (!value) return "Date of Birth is required";
                          const selectedDate = new Date(value);
                          const currentDate = new Date();
                          const age =
                            currentDate.getFullYear() -
                            selectedDate.getFullYear();
                          const monthDiff =
                            currentDate.getMonth() - selectedDate.getMonth();

                          // Calculate exact age considering month and day
                          const exactAge =
                            monthDiff < 0 ||
                            (monthDiff === 0 &&
                              currentDate.getDate() < selectedDate.getDate())
                              ? age - 1
                              : age;

                          if (exactAge < 13) {
                            return "Minimum age required is 13 years";
                          }
                          return true;
                        },
                      })}
                      max={(() => {
                        const currentDate = new Date();
                        const maxDate = new Date(
                          currentDate.getFullYear() - 13,
                          currentDate.getMonth(),
                          currentDate.getDate()
                        );
                        return maxDate.toISOString().split("T")[0];
                      })()}
                      value={formData.dob}
                      onChange={handleChange}
                    />
                    {errors.dob && (
                      <p className="text-red-500 text-sm">
                        {errors.dob.message}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-label text-base font-semibold">
                      Gender
                    </label>
                    <select
                      className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                      {...register("gender", {
                        required: "Gender is required",
                      })}
                      value={formData.gender}
                      onChange={handleChange}
                    >
                      <option value="">Select Gender</option>
                      {Object.values(GENDERS).map((gender, index) => {
                        return (
                          <option value={gender} key={index}>
                            {gender}
                          </option>
                        );
                      })}
                    </select>
                    {errors.gender && (
                      <p className="text-red-500 text-sm">
                        {errors.gender.message}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <PhoneNumberInput
                      control={control}
                      name="phone"
                      label="Phone Number"
                      error={errors.phone}
                      placeholder="(123) 456-7890"
                      required={true}
                      className="custom-input"
                      labelClassName="custom-label"
                      gap="gap-1"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-label text-base font-semibold">
                      Language
                    </label>
                    <select
                      className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                      {...register("language", {
                        required: "Language is required",
                      })}
                      value={formData.language}
                      onChange={handleChange}
                    >
                      <option value="">Select Language</option>
                      {Object.values(LANGUAGES).map((lang, index) => {
                        return <option key={index}>{lang}</option>;
                      })}
                    </select>
                    {errors.language && (
                      <p className="text-red-500 text-sm">
                        {errors.language.message}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-label text-base font-semibold">
                      Address
                    </label>
                    <input
                      type="text"
                      className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                      {...register("address", {
                        required: "address is required",
                      })}
                      value={formData.address}
                      onChange={handleChange}
                    />
                    {errors.address && (
                      <p className="text-red-500 text-sm">
                        {errors.address.message}
                      </p>
                    )}
                  </div>
                  {/* State Dropdown */}
                  <div className="flex flex-col gap-1">
                    <label className="text-label text-base font-semibold">
                      State
                    </label>
                    <select
                      className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                      {...register("state", {
                        required: "State is required",
                      })}
                      value={formData.state}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      {states.map((state, index) => (
                        <option key={index} value={state.name}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                    {errors.state && (
                      <p className="text-red-500 text-sm">
                        {errors.state.message}
                      </p>
                    )}
                  </div>

                  {/* City Dropdown */}
                  <div className="flex flex-col gap-1">
                    <label className="text-label text-base font-semibold">
                      City
                    </label>
                    <select
                      className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                      {...register("city", {
                        required: false,
                      })}
                      value={formData.city}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      {cities.map((city, index) => (
                        <option key={index} value={city.name}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                    {errors.city && (
                      <p className="text-red-500 text-sm">
                        {errors.city.message}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-label text-base font-semibold">
                      Zip Code
                    </label>
                    <input
                      type="text"
                      className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                      {...register("zipCode", {
                        required: "Zip code is required",
                        pattern: {
                          value: /^[0-9]{5}(?:-[0-9]{4})?$/,
                          message: "Please enter a valid ZIP code",
                        },
                      })}
                      value={formData.zipCode}
                      onChange={handleChange}
                    />
                    {errors.zipCode && (
                      <p className="text-red-500 text-sm">
                        {errors.zipCode.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Credential Information */}
          {currentStep === 2 && (
            <div className="w-full">
              <div className="w-full my-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <MultiselectField
                  error={errors.skills}
                  value={formData.skills}
                  label="Select Skills/Strength"
                  valueProperty="title"
                  helperText="Select skill(s)"
                  options={skills.map((s) => ({
                    ...s,
                    title: s.title.replace(/_/g, " "),
                  }))}
                  onSelect={(s) => onSelectSkills(s)}
                />
              </div>
              <div className="w-full my-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex flex-col gap-1 relative">
                  <div className="flex items-center justify-between">
                    <label className="text-label text-base font-semibold">
                      Mission
                    </label>
                    <span className=" text-primaryBlueText text-xs">
                      {formData.mission ? formData.mission.length : 0}/
                      {maxLength}
                    </span>
                  </div>
                  <input
                    type="text"
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none "
                    {...register("mission", {
                      required: "mission is required",
                    })}
                    value={formData.mission}
                    onChange={handleChange}
                    maxLength={maxLength}
                  />
                  {/* <span className="absolute right-3 bottom-3 text-primaryBlueText text-sm z-20">
                    {formData.mission ? formData.mission.length : 0}/{maxLength}
                  </span> */}
                  {errors.mission && (
                    <p className="text-red-500 text-sm">
                      {errors.mission.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="w-full my-4  grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    Select Level of Engagement
                  </label>
                  <select
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    {...register("engagementLevel", {
                      required: "Level of Engagement is required",
                    })}
                    value={formData.engagementLevel}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {engagementOptions.map((option, index) => {
                      return (
                        <option key={index} value={option}>
                          {option.replace(/_/g, " ")}
                        </option>
                      );
                    })}
                  </select>
                  {errors.engagementLevel && (
                    <p className="text-red-500 text-sm">
                      {errors.engagementLevel.message}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    Select Length of Commitment
                  </label>
                  <select
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    {...register("commitmentLength", {
                      required: "Commitment is required",
                    })}
                    value={formData.commitmentLength}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {commitmentOptions.map((option, index) => {
                      return (
                        <option key={index} value={option}>
                          {option.replace(/_/g, " ")}
                        </option>
                      );
                    })}
                  </select>
                  {errors.commitmentLength && (
                    <p className="text-red-500 text-sm">
                      {errors.commitmentLength.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="w-full my-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="flex flex-col gap-1">
                  <label className="text-label text-base font-semibold">
                    Is there an ambassador that should be thanked for referring
                    you? (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Ambassador Referral code"
                    className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                    {...register("referralCode")}
                    value={formData.referralCode}
                    onChange={handleChange}
                  />
                  {errors.referralCode && (
                    <p className="text-red-500 text-sm">
                      {errors.referralCode.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="mt-6 flex gap-3">
          {currentStep > 1 && (
            <button
              type="button"
              className="px-4 py-2 bg-primaryBlueBg text-white rounded-full"
              onClick={handlePrevious}
            >
              Previous
            </button>
          )}
          {currentStep < 2 ? (
            <button
              type="button"
              onClick={handleSubmit((data) => handleNext(data))}
              className="px-4 py-2 bg-primaryBlueBg text-white rounded-full"
            >
              Next
            </button>
          ) : (
            <button
              onClick={() => {
                navigate(siteRoutes.ambassadorWelcome);
              }}
              type="submit"
              className="px-4 py-2 bg-primaryBlueBg text-white rounded-full"
            >
              Submit
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default AmbassadorProfile;
